import React, { useState, useEffect, useRef } from 'react';
import { api } from '../lib/apiClient';
import { Truck, Users, Route, Star, ShieldCheck, Award } from 'lucide-react';

const useCountUp = (end, active, duration = 1600) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active || !end) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.floor(eased * end));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [end, active, duration]);
  return value;
};

const formatNum = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(0)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return `${n}`;
};

const StatCard = ({ icon: Icon, end, suffix = '', label, active }) => {
  const value = useCountUp(end, active);
  return (
    <div className="text-center" data-testid={`stat-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <Icon className="h-9 w-9 text-orange-400 mx-auto mb-3" />
      <div className="text-4xl lg:text-5xl font-bold text-white">
        {end >= 1000 ? formatNum(value) : value}{suffix}
      </div>
      <div className="text-blue-200 text-sm mt-1">{label}</div>
    </div>
  );
};

export const StatsSection = () => {
  const [stats, setStats] = useState(null);
  const [active, setActive] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    api.get('/site/stats').then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setActive(true); }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  if (!stats) return <div ref={ref} className="h-40 bg-blue-900" />;

  return (
    <section ref={ref} className="bg-blue-900 py-16" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          <StatCard icon={Award} end={stats.years_in_business} suffix="+" label="Years in Business" active={active} />
          <StatCard icon={Truck} end={stats.loads_booked} suffix="+" label="Loads Booked" active={active} />
          <StatCard icon={Users} end={stats.active_carriers} suffix="+" label="Active Carriers" active={active} />
          <StatCard icon={Route} end={stats.miles_dispatched} suffix="+" label="Miles Dispatched" active={active} />
        </div>
        <div className="flex flex-wrap justify-center gap-4 border-t border-blue-800 pt-10">
          <div className="flex items-center gap-2 bg-blue-800/50 rounded-full px-5 py-2.5 text-white text-sm" data-testid="badge-google">
            <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
            <span className="font-semibold">{stats.google_rating}</span>
            <span className="text-blue-200">Google ({stats.google_review_count} reviews)</span>
          </div>
          <div className="flex items-center gap-2 bg-blue-800/50 rounded-full px-5 py-2.5 text-white text-sm" data-testid="badge-fmcsa">
            <ShieldCheck className="h-4 w-4 text-green-400" />
            <span className="font-semibold">FMCSA</span>
            <span className="text-blue-200">{stats.fmcsa_status}</span>
          </div>
          <div className="flex items-center gap-2 bg-blue-800/50 rounded-full px-5 py-2.5 text-white text-sm" data-testid="badge-safety">
            <Award className="h-4 w-4 text-green-400" />
            <span className="font-semibold">DOT Safety:</span>
            <span className="text-blue-200">{stats.safety_rating}</span>
          </div>
          <div className="flex items-center gap-2 bg-blue-800/50 rounded-full px-5 py-2.5 text-white text-sm" data-testid="badge-ontime">
            <Truck className="h-4 w-4 text-orange-400" />
            <span className="font-semibold">{stats.on_time_rate}%</span>
            <span className="text-blue-200">On-Time</span>
          </div>
        </div>
      </div>
    </section>
  );
};
